import { useCallback, useRef } from 'react';
import { useMutation } from 'urql';

import { RequestBookingTaskFileUploadDownload } from 'containers/Studies/Results/Unmoderated/data/RequestBookingTaskFileUploadDownload.mutation';

/**
 * What a press did. `ignored` keeps a press that a download in flight absorbed apart from a
 * failure, so the caller does not report an error for it.
 */
export type DownloadOutcome = 'started' | 'failed' | 'ignored';

/**
 * Downloads the file on a file-upload task result.
 *
 * `requestBookingTaskFileUploadDownload` checks the caller's team access and mints a presigned
 * S3 GET that expires in minutes, so the URL is requested on each press and not held in state.
 * Its `attachment` disposition lets the browser download it without leaving the page.
 * A press during a download is ignored. The guard is a ref, not `isDownloading`, because a
 * caller can keep the button live to stop a quick download flashing a disabled state, and
 * `isDownloading` only turns true on the next render.
 */
export const useFileUploadDownload = () => {
  const [{ fetching }, requestDownload] = useMutation(RequestBookingTaskFileUploadDownload);
  const isInFlight = useRef(false);

  const download = useCallback(
    async (taskResultId: string): Promise<DownloadOutcome> => {
      if (isInFlight.current) {
        return 'ignored';
      }
      isInFlight.current = true;

      try {
        const result = await requestDownload({ input: { _task_result_id: taskResultId } });
        const url = result.data?.requestBookingTaskFileUploadDownload?.download_url;

        if (!url) {
          return 'failed';
        }

        window.location.href = url;

        return 'started';
      } finally {
        isInFlight.current = false;
      }
    },
    [requestDownload],
  );

  return { download, isDownloading: fetching };
};
